import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DonutChart from "../../components/DonutChart";
import { useAuth } from "../../context/AuthContext";
import { analyticsAPI } from "../../utils/analyticsAPI";
import { salesAPI } from "../../utils/salesAPI";
import { showToast } from "../../components/Toast";
import { useTranslation } from "react-i18next";

function ClientDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [salesSummary, setSalesSummary] = useState(null);

  const organizationId = useMemo(() => {
    if (user?.organization && typeof user.organization === "object") {
      return user.organization.id;
    }
    return user?.organization || null;
  }, [user]);

  useEffect(() => {
    if (organizationId) {
      loadDashboardData();
    }
  }, [organizationId]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [dashResponse, activitiesResponse, salesResponse] =
        await Promise.all([
          analyticsAPI.getDashboard(organizationId),
          analyticsAPI.getRecentActivities(organizationId),
          salesAPI.getSummary(organizationId).catch(() => null),
        ]);

      setDashboardData(dashResponse?.data || dashResponse);
      setActivities(activitiesResponse?.data || activitiesResponse || []);
      const salesData = salesResponse?.data || salesResponse;
      setSalesSummary(salesData || null);
    } catch (error) {
      console.error("Failed to load dashboard:", error);
      showToast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getTimeAgo = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60)
      return `${diffMins} ${diffMins === 1 ? "minute" : "minutes"} ago`;
    if (diffHours < 24)
      return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
    return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
  };

  // Prepare pie chart data for inventory status
  const inventoryChartData = dashboardData?.inventory
    ? {
        labels: ["Finished", "Urgent", "GIP (Good in Progress)", "Dead"],
        datasets: [
          {
            data: [
              dashboardData.inventory.status_breakdown?.finished || 0,
              dashboardData.inventory.status_breakdown?.urgent || 0,
              dashboardData.inventory.status_breakdown?.gip || 0,
              dashboardData.inventory.status_breakdown?.dead || 0,
            ],
            backgroundColor: [
              "rgba(34, 197, 94, 0.85)", // Green for finished
              "rgba(239, 68, 68, 0.85)", // Red for urgent
              "rgba(59, 130, 246, 0.85)", // Blue for GIP
              "rgba(107, 114, 128, 0.85)", // Gray for dead
            ],
            borderColor: [
              "rgb(34, 197, 94)",
              "rgb(239, 68, 68)",
              "rgb(59, 130, 246)",
              "rgb(107, 114, 128)",
            ],
            borderWidth: 2,
            hoverBorderWidth: 3,
            hoverOffset: 8,
          },
        ],
      }
    : null;

  // Prepare sales payment method pie chart data
  const salesChartData = salesSummary
    ? (() => {
        const labels = [];
        const values = [];
        const colors = [];
        const borderColors = [];
        const colorMap = {
          CASH: ["rgba(34, 197, 94, 0.85)", "rgb(34, 197, 94)"],
          CARD: ["rgba(59, 130, 246, 0.85)", "rgb(59, 130, 246)"],
          MOBILE_MONEY: ["rgba(168, 85, 247, 0.85)", "rgb(168, 85, 247)"],
          BANK_TRANSFER: ["rgba(245, 158, 11, 0.85)", "rgb(245, 158, 11)"],
          CREDIT: ["rgba(239, 68, 68, 0.85)", "rgb(239, 68, 68)"],
          OTHER: ["rgba(107, 114, 128, 0.85)", "rgb(107, 114, 128)"],
        };
        const fallbackColors = [
          ["rgba(16, 185, 129, 0.85)", "rgb(16, 185, 129)"],
          ["rgba(99, 102, 241, 0.85)", "rgb(99, 102, 241)"],
          ["rgba(236, 72, 153, 0.85)", "rgb(236, 72, 153)"],
        ];

        const breakdown = salesSummary.payment_breakdown || [];
        breakdown.forEach((entry, i) => {
          const method = entry.payment_method || "OTHER";
          labels.push(method.replace(/_/g, " "));
          values.push(parseFloat(entry.amount || 0));
          const cm =
            colorMap[method] || fallbackColors[i % fallbackColors.length];
          colors.push(cm[0]);
          borderColors.push(cm[1]);
        });

        return labels.length > 0
          ? {
              labels,
              datasets: [
                {
                  data: values,
                  backgroundColor: colors,
                  borderColor: borderColors,
                  borderWidth: 2,
                  hoverBorderWidth: 3,
                  hoverOffset: 8,
                },
              ],
            }
          : null;
      })()
    : null;

  if (loading) {
    return (
      <div className="page-content">
        <div className="card">
          <p>{t("dashboard.loading", "Loading dashboard...")}</p>
        </div>
      </div>
    );
  }

  if (!organizationId) {
    return (
      <div className="page-content">
        <div className="card">
          <p>
            {t(
              "dashboard.noOrganization",
              "No organization assigned. Please contact your administrator.",
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="greeting-section" style={{ marginBottom: "32px" }}>
        <h1>
          {t("dashboard.welcomeBack", "Welcome back")},{" "}
          {user?.first_name ||
            user?.name ||
            t("dashboard.clientFallback", "Client")}
          !
        </h1>
        <p>
          {user?.organization_name &&
            t("dashboard.organizationPrefix", "Organization: ") +
              user.organization_name +
              ". "}
          {t(
            "dashboard.keepTrack",
            "Keep track of your datasets, reports, and inventory below.",
          )}
        </p>
      </div>

      <div className="stats-grid">
        <div
          className="stat-card"
          role="button"
          tabIndex={0}
          onClick={() => navigate("/client/data-entry")}
          onKeyDown={(e) => e.key === "Enter" && navigate("/client/data-entry")}
        >
          <div className="stat-label">
            {t("dashboard.totalDatasets", "Total Datasets")}
          </div>
          <div className="stat-value">
            {dashboardData?.datasets?.total_datasets || 0}
          </div>
          <div className="stat-change">
            {dashboardData?.activity?.new_datasets_30d || 0}{" "}
            {t("dashboard.thisMonth", "this month")}
          </div>
        </div>

        <div
          className="stat-card"
          role="button"
          tabIndex={0}
          onClick={() => navigate("/client/data-entry")}
          onKeyDown={(e) => e.key === "Enter" && navigate("/client/data-entry")}
        >
          <div className="stat-label">{t("data.rows", "Data Rows")}</div>
          <div className="stat-value">
            {dashboardData?.datasets?.total_rows?.toLocaleString() || 0}
          </div>
          <div className="stat-change">
            {dashboardData?.activity?.new_rows_30d || 0}{" "}
            {t("dashboard.thisMonth", "this month")}
          </div>
        </div>

        <div
          className="stat-card"
          role="button"
          tabIndex={0}
          onClick={() => navigate("/client/reports")}
          onKeyDown={(e) => e.key === "Enter" && navigate("/client/reports")}
        >
          <div className="stat-label">
            {t("dashboard.reportsGenerated", "Reports Generated")}
          </div>
          <div className="stat-value">
            {dashboardData?.reports?.total_reports || 0}
          </div>
          <div className="stat-change">
            {dashboardData?.reports?.draft_reports || 0}{" "}
            {t("dashboard.pendingReview", "pending review")}
          </div>
        </div>

        <div
          className="stat-card"
          role="button"
          tabIndex={0}
          onClick={() => navigate("/client/sales")}
          onKeyDown={(e) => e.key === "Enter" && navigate("/client/sales")}
        >
          <div className="stat-label">
            {t("sales.totalRevenue", "Total Sales Revenue")}
          </div>
          <div className="stat-value">
            {salesSummary
              ? new Intl.NumberFormat("en-RW", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(salesSummary.total_revenue || 0) + " RWF"
              : "0 RWF"}
          </div>
          <div className="stat-change">
            {salesSummary?.total_sales || 0}{" "}
            {t("sales.totalSales", "Total Sales")}
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            inventoryChartData && salesChartData ? "1fr 1fr" : "1fr",
          gap: "24px",
          marginTop: "24px",
        }}
      >
        {/* Inventory Status Chart */}
        {inventoryChartData && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                {t(
                  "dashboard.inventoryStatusDistribution",
                  "Inventory Status Distribution",
                )}
              </h3>
            </div>
            <div style={{ padding: "20px" }}>
              <DonutChart data={inventoryChartData} height="300px" />
            </div>
          </div>
        )}

        {/* Sales Overview Chart */}
        {salesChartData && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                {t("dashboard.salesOverview", "Sales Overview")}
              </h3>
            </div>
            <div style={{ padding: "20px" }}>
              <DonutChart data={salesChartData} height="300px" />
            </div>
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: "24px" }}>
        <div className="card-header">
          <h3 className="card-title">
            {t("dashboard.recentActivity", "Recent Activity")}
          </h3>
        </div>
        <div className="activity-list">
          {activities.length > 0 ? (
            activities.slice(0, 8).map((item, idx) => (
              <div key={idx} className="activity-item">
                <div>
                  <div className="activity-action">{item.action}</div>
                  <div className="activity-dataset">{item.dataset}</div>
                </div>
                <div className="activity-time">
                  {getTimeAgo(item.timestamp)}
                </div>
              </div>
            ))
          ) : (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                color: "var(--text-grey)",
              }}
            >
              {t("dashboard.noRecentActivity", "No recent activity")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClientDashboard;
